/**
 * GenericControlConfigPanel - Works for AI_02, AI_03, AI_04, AI_05, AI_06, AI_10
 */
import React, { useState, useEffect } from 'react';
import { Card, Switch, Button, Collapse, Input, Tag, Alert, Spin } from 'antd';
import {
  SettingOutlined,
  CheckCircleOutlined,
  WarningOutlined,
  SaveOutlined
} from '@ant-design/icons';
import { getControlConfig, saveControlConfig } from '../api/trustgridApi';
import { showError, showSuccess } from '../../../utils/notifications';

const { Panel } = Collapse;
const { TextArea } = Input;

// Control-specific metadata
const CONTROL_INFO = {
  'AI_02': {
    title: 'Model Risk Classification',
    description: 'Classify models by risk level (unacceptable, high, limited, minimal) based on EU AI Act and organizational policies.'
  },
  'AI_03': {
    title: 'Model Provenance & Versioning',
    description: 'Track model lineage, versions, training data, and approval records for reproducibility and audit.'
  },
  'AI_04': {
    title: 'Explainability & Transparency',
    description: 'Ensure model predictions are explainable using methods like SHAP, LIME, or confidence scores.'
  },
  'AI_05': {
    title: 'Training Data Audit Trail',
    description: 'Maintain audit trail of training data sources, quality scores, consent status, and lineage.'
  },
  'AI_06': {
    title: 'Model Performance Monitoring',
    description: 'Monitor model performance metrics (accuracy, latency, throughput) and detect drift over time.'
  },
  'AI_10': {
    title: 'Incident Response & Rollback',
    description: 'Enable quick rollback of model deployments and automated incident response for production issues.'
  }
};

function GenericControlConfigPanel({ projectId, controlCode, project }) {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [config, setConfig] = useState(null);
  const [hasChanges, setHasChanges] = useState(false);
  const [activeKeys, setActiveKeys] = useState(['1']);

  const controlInfo = CONTROL_INFO[controlCode] || { title: controlCode, description: '' };

  useEffect(() => {
    if (projectId && controlCode) {
      fetchConfig();
    }
  }, [projectId, controlCode]);

  const fetchConfig = async () => {
    setLoading(true);
    try {
      const response = await getControlConfig(projectId, controlCode);
      setConfig(response.data);
    } catch (error) {
      console.error('Failed to fetch config:', error);
      setConfig({
        enabled: false,
        config: {}
      });
      if (error.response && error.response.status !== 404) {
        showError('Failed to load configuration. Using defaults.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await saveControlConfig(projectId, controlCode, config);
      showSuccess('Configuration saved successfully');
      setHasChanges(false);
      fetchConfig();
    } catch (error) {
      console.error('Failed to save config:', error);
      showError(error);
    } finally {
      setSaving(false);
    }
  };

  const updateConfig = (field, value) => {
    setConfig({
      ...config,
      config: {
        ...config.config,
        [field]: value
      }
    });
    setHasChanges(true);
  };

  if (loading) {
    return (
      <Card>
        <div style={{ display: 'flex', justifyContent: 'center', padding: '40px' }}>
          <Spin size="large" />
        </div>
      </Card>
    );
  }

  if (!config) return null;

  const configData = config.config || {};
  const isConfigured = config.enabled;
  const statusIcon = isConfigured ? <CheckCircleOutlined style={{ color: '#52c41a' }} /> : <WarningOutlined style={{ color: '#faad14' }} />;
  const statusText = isConfigured ? 'Active' : 'Disabled';

  return (
    <Card
      title={
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <SettingOutlined />
          <span>{controlCode} Configuration: {controlInfo.title}</span>
          {statusIcon}
          <Tag color={isConfigured ? 'green' : 'gray'}>{statusText}</Tag>
        </div>
      }
      extra={
        <Button
          type="primary"
          icon={<SaveOutlined />}
          onClick={handleSave}
          loading={saving}
          disabled={!hasChanges}
        >
          Save Changes
        </Button>
      }
    >
      <Collapse activeKey={activeKeys} onChange={setActiveKeys} expandIconPosition="right">
        <Panel
          header={
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <strong>1. Enable {controlInfo.title}</strong>
              {config.enabled && <Tag color="green">Active</Tag>}
            </div>
          }
          key="1"
        >
          <div style={{ padding: '16px 0' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '16px' }}>
              <Switch
                checked={config.enabled}
                onChange={(checked) => setConfig({ ...config, enabled: checked })}
                size="large"
              />
              <div>
                <div style={{ fontWeight: 'bold', marginBottom: '4px' }}>
                  {config.enabled ? 'Control Enabled' : 'Control Disabled'}
                </div>
                <div style={{ color: '#888', fontSize: '13px' }}>
                  {controlInfo.description}
                </div>
              </div>
            </div>

            {!config.enabled && (
              <Alert
                type="info"
                message={`About ${controlInfo.title}`}
                description={
                  <div>
                    <p>{controlInfo.description}</p>
                    <p style={{ marginTop: '8px' }}>
                      Enable this control to start monitoring and enforcing compliance.
                      Configure advanced options after enabling.
                    </p>
                  </div>
                }
                showIcon
                style={{ marginTop: '16px' }}
              />
            )}
          </div>
        </Panel>

        <Panel
          header={
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <strong>2. Advanced Configuration</strong>
              {Object.keys(configData).length > 0 && <Tag color="blue">{Object.keys(configData).length} settings</Tag>}
            </div>
          }
          key="2"
          disabled={!config.enabled}
        >
          <div style={{ padding: '16px 0' }}>
            <div style={{ marginBottom: '24px' }}>
              <label style={{ display: 'block', fontWeight: 'bold', marginBottom: '8px' }}>
                Configuration (JSON)
              </label>
              <TextArea
                value={JSON.stringify(configData, null, 2)}
                onChange={(e) => {
                  try {
                    const parsed = JSON.parse(e.target.value);
                    setConfig({ ...config, config: parsed });
                    setHasChanges(true);
                  } catch (err) {
                    // Invalid JSON, don't update
                  }
                }}
                rows={10}
                style={{ fontFamily: 'monospace', fontSize: '12px' }}
              />
              <div style={{ fontSize: '12px', color: '#888', marginTop: '8px' }}>
                Edit control configuration as JSON. See control schema for available options.
              </div>
            </div>

            <Alert
              type="info"
              message="Configuration Schema"
              description={
                <div>
                  <p>This control uses a JSON configuration schema. Key fields vary by control:</p>
                  <ul style={{ marginTop: '8px', marginBottom: '0' }}>
                    <li><strong>AI_02:</strong> risk_levels_required, high_risk_requires_oversight, classification_criteria</li>
                    <li><strong>AI_03:</strong> required_provenance_fields, versioning_format, track_training_data</li>
                    <li><strong>AI_04:</strong> required_explanation_methods, min_confidence_threshold</li>
                    <li><strong>AI_05:</strong> required_audit_fields, require_consent, min_quality_score</li>
                    <li><strong>AI_06:</strong> required_metrics, degradation_threshold_pct, enable_drift_detection</li>
                    <li><strong>AI_10:</strong> enable_rollback, deployment_strategy, keep_previous_versions</li>
                  </ul>
                  <p style={{ marginTop: '8px', marginBottom: '0' }}>
                    See <code>control_config_schemas.py</code> for complete schema definitions.
                  </p>
                </div>
              }
              showIcon
              style={{ marginTop: '16px' }}
            />
          </div>
        </Panel>
      </Collapse>
    </Card>
  );
}

export default GenericControlConfigPanel;
